import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const defaultActions = {
  onRefresh: null,
  onFilter: null,
  refreshing: false,
};

const ControladoriaPageActionsContext = createContext({
  actions: defaultActions,
  setPageActions: () => {},
  clearPageActions: () => {},
});

export const ControladoriaPageActionsProvider = ({ children }) => {
  const [actions, setActions] = useState(defaultActions);

  const setPageActions = useCallback((next) => {
    setActions((prev) => ({
      ...prev,
      ...next,
    }));
  }, []);

  const clearPageActions = useCallback(() => {
    setActions(defaultActions);
  }, []);

  const value = useMemo(
    () => ({ actions, setPageActions, clearPageActions }),
    [actions, setPageActions, clearPageActions]
  );

  return (
    <ControladoriaPageActionsContext.Provider value={value}>
      {children}
    </ControladoriaPageActionsContext.Provider>
  );
};

export const useControladoriaHeaderActions = () => {
  const { actions } = useContext(ControladoriaPageActionsContext);
  return actions;
};

/**
 * Registra refresh/filtro da rotina atual no header MUI.
 * Usa refs para não re-registrar a cada render da página.
 */
export const useRegisterControladoriaPageActions = ({
  onRefresh,
  onFilter,
  refreshing = false,
}) => {
  const { setPageActions, clearPageActions } = useContext(
    ControladoriaPageActionsContext
  );

  const refreshRef = useRef(onRefresh);
  const filterRef = useRef(onFilter);
  refreshRef.current = onRefresh;
  filterRef.current = onFilter;

  const canRefresh = typeof onRefresh === "function";
  const canFilter = typeof onFilter === "function";

  useEffect(() => {
    setPageActions({
      onRefresh: canRefresh ? () => refreshRef.current?.() : null,
      onFilter: canFilter ? () => filterRef.current?.() : null,
      refreshing: Boolean(refreshing),
    });
  }, [canRefresh, canFilter, refreshing, setPageActions]);

  useEffect(() => () => clearPageActions(), [clearPageActions]);
};
